import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils";
import { WorkOrderIntakeDropzone } from "@/components/work-order-intake-dropzone";
import {
    Inbox,
    FileText,
    Sparkles,
    CalendarClock,
    PhoneCall,
    Mail,
    Globe,
    AlertTriangle,
    ArrowRight,
    CheckCircle2,
    Loader2,
} from "lucide-react";

interface WoQueueItem {
    id: string;
    status: "ready_to_confirm" | "needs_review";
    overall_confidence: number | null;
    can_auto_confirm: boolean;
    confidence_flags: Array<{ code: string; severity: string; message: string }>;
    vendor_name: string | null;
    site_name: string | null;
    work_order_number: string | null;
    due_date: string | null;
}

interface WoQueueSummary {
    ready: WoQueueItem[];
    needsReview: WoQueueItem[];
}

function confidencePct(c: number | null): string {
    if (c == null) return "—";
    return `${Math.round(c * 100)}%`;
}

/**
 * Work-order intake, grouped by auto-confirm readiness. "Ready to confirm"
 * items are high-confidence auto matches that can be turned into jobs in one
 * click ("Confirm all" -> batch-confirm). "Needs review" items open the
 * full-screen setup page.
 */
function WorkOrderQueueSummary() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [confirming, setConfirming] = useState(false);

    const summary = useQuery({
        queryKey: ["work-order-intake", "queue-summary"],
        queryFn: () => api.get<WoQueueSummary>("/api/work-order-intake/queue-summary"),
        refetchInterval: 30_000,
    });

    const ready = summary.data?.ready ?? [];
    const needsReview = summary.data?.needsReview ?? [];

    async function confirmAll() {
        const itemIds = ready.map((r) => r.id);
        if (itemIds.length === 0) return;
        setConfirming(true);
        try {
            const res = await api.post<{ created: string[]; failed: Array<{ itemId: string; reason: string }> }>(
                "/api/work-order-intake/batch-confirm",
                { itemIds },
            );
            if (res.created.length > 0) {
                toast.success(
                    `${res.created.length} job${res.created.length === 1 ? "" : "s"} created from work orders.`,
                );
            }
            if (res.failed.length > 0) {
                toast.error(`${res.failed.length} could not be confirmed — open them to review.`);
            }
            void qc.invalidateQueries({ queryKey: ["work-order-intake", "queue-summary"] });
            void qc.invalidateQueries({ queryKey: ["work-pulse"] });
            void qc.invalidateQueries({ queryKey: ["intake", "queue"] });
        } catch {
            toast.error("Batch confirm failed.");
        } finally {
            setConfirming(false);
        }
    }

    function confirmOne(itemId: string) {
        navigate(`/jobs/work-orders/setup/${itemId}`);
    }

    if (summary.isLoading) return null;
    // A failed fetch must be visible — silently rendering nothing looks like
    // an empty queue while the backend is down.
    if (summary.isError) {
        return (
            <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2">
                <span className="text-xs font-medium text-destructive">
                    Couldn't load the work order queue —{" "}
                    {summary.error instanceof Error ? summary.error.message : "unknown error"}
                </span>
                <Button size="sm" variant="outline" onClick={() => void summary.refetch()}>
                    Retry
                </Button>
            </div>
        );
    }
    if (ready.length === 0 && needsReview.length === 0) return null;

    return (
        <div className="mb-3 space-y-3">
            {ready.length > 0 && (
                <div className="rounded-lg border border-emerald-500/40 bg-card p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                            <span className="text-sm font-semibold">
                                {ready.length} work order{ready.length === 1 ? "" : "s"} ready (auto-matched)
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Button size="sm" disabled={confirming} onClick={() => void confirmAll()}>
                                {confirming ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <CheckCircle2 className="h-3 w-3" />
                                )}
                                Confirm all
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => navigate("/jobs/work-orders")}>
                                Review individually
                            </Button>
                        </div>
                    </div>
                    <ul className="space-y-1">
                        {ready.map((it) => (
                            <li
                                key={it.id}
                                className="flex items-center justify-between gap-3 rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-xs"
                            >
                                <div className="min-w-0">
                                    <span className="font-semibold">{it.vendor_name ?? "Vendor"}</span>
                                    {it.site_name && <span className="text-foreground/70"> · {it.site_name}</span>}
                                    {it.due_date && (
                                        <span className="text-foreground/60"> · due {formatDate(it.due_date)}</span>
                                    )}
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <span className="font-semibold tabular-nums text-emerald-600">
                                        {confidencePct(it.overall_confidence)}
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-2"
                                        onClick={() => confirmOne(it.id)}
                                    >
                                        Review
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {needsReview.length > 0 && (
                <div className="rounded-lg border border-amber-500/40 bg-card p-3">
                    <div className="mb-2 flex items-center gap-2">
                        <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                        <span className="text-sm font-semibold">
                            {needsReview.length} work order{needsReview.length === 1 ? "" : "s"} need setup
                        </span>
                    </div>
                    <ul className="space-y-1">
                        {needsReview.map((it) => (
                            <li
                                key={it.id}
                                className="flex items-center justify-between gap-3 rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-xs"
                            >
                                <div className="min-w-0">
                                    <span className="font-semibold">{it.vendor_name ?? "Vendor"}</span>
                                    {it.site_name ? (
                                        <span className="text-foreground/70"> · {it.site_name}</span>
                                    ) : (
                                        <span className="text-amber-600"> · no site match</span>
                                    )}
                                    {it.work_order_number && (
                                        <span className="text-foreground/60"> · WO {it.work_order_number}</span>
                                    )}
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 shrink-0 px-2"
                                    onClick={() => navigate(`/jobs/work-orders/setup/${it.id}`)}
                                >
                                    Review
                                    <ArrowRight className="h-3 w-3" />
                                </Button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

type IntakeSource = "work_order" | "lead" | "booking" | "call";

interface IntakeQueueItem {
    id: string;
    source: IntakeSource;
    sourceLabel: string;
    customerId: string | null;
    customerName: string | null;
    rawSummary: string;
    createdAt: string;
    missingFields: string[];
    // Booking-only signals (Phase 3 discoverability).
    cardOnFile?: boolean;
    cardRequired?: boolean;
    cardBrand?: string | null;
    cardLast4?: string | null;
    smsOptIn?: boolean;
}

interface IntakeQueueResponse {
    items: IntakeQueueItem[];
    total: number;
}

const SOURCE_META: Record<IntakeSource, { icon: React.ComponentType<{ className?: string }>; badgeClass: string }> = {
    work_order: { icon: FileText, badgeClass: "border-violet-500/20 bg-violet-500/10 text-violet-600" },
    lead: { icon: Sparkles, badgeClass: "border-emerald-500/20 bg-emerald-500/10 text-emerald-600" },
    booking: { icon: CalendarClock, badgeClass: "border-sky-500/20 bg-sky-500/10 text-sky-600" },
    call: { icon: PhoneCall, badgeClass: "border-amber-500/20 bg-amber-500/10 text-amber-600" },
};

// Sources we intend to ingest but that have no backend yet — surfaced as
// disabled chips so the office can see the full intended intake set.
const FUTURE_SOURCES: Array<{ label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { label: "Email", icon: Mail },
    { label: "Portal", icon: Globe },
];

function IntakeRow({
    item,
    onProcess,
    busy,
}: {
    item: IntakeQueueItem;
    onProcess: (item: IntakeQueueItem) => void;
    busy: boolean;
}) {
    const meta = SOURCE_META[item.source];
    const Icon = meta.icon;
    const blocked = item.missingFields.length > 0;
    const isWorkOrder = item.source === "work_order";
    // Work orders never create a job from here — they always open the review
    // flow first (see handleProcess). Other sources still go to the job form.
    const actionLabel = isWorkOrder ? "Review" : "Create job";

    return (
        <div
            className={`flex items-start justify-between gap-4 rounded-lg border p-3 transition-colors ${
                blocked
                    ? "border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/15"
                    : "border-border/60 hover:bg-secondary/50"
            }`}
        >
            <div className="min-w-0 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                    <span
                        className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.badgeClass}`}
                    >
                        <Icon className="h-3 w-3" />
                        {item.sourceLabel}
                    </span>
                    {item.customerName ? (
                        <span className="text-sm font-semibold truncate">{item.customerName}</span>
                    ) : (
                        <span className="text-sm font-semibold italic text-foreground/60">Unrecognized</span>
                    )}
                    {item.source === "booking" && item.cardOnFile && (
                        <span className="inline-flex items-center rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                            ✓{" "}
                            {[item.cardBrand, item.cardLast4 && `••${item.cardLast4}`].filter(Boolean).join(" ") ||
                                "Card on file"}
                        </span>
                    )}
                    {item.source === "booking" && !item.cardOnFile && item.cardRequired && (
                        <span className="inline-flex items-center rounded-md border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:text-red-300">
                            No card yet
                        </span>
                    )}
                    {item.source === "booking" && item.smsOptIn && (
                        <span className="inline-flex items-center rounded-md border border-sky-500/30 bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700 dark:text-sky-300">
                            💬 Text OK
                        </span>
                    )}
                    {blocked && (
                        <Badge variant="warning" className="gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Pending
                        </Badge>
                    )}
                </div>
                <p className="text-sm text-foreground/80 line-clamp-2">{item.rawSummary}</p>
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-foreground/60">{formatDate(item.createdAt)}</span>
                    {item.missingFields.map((f) => (
                        <span
                            key={f}
                            className="inline-flex items-center rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300"
                        >
                            {f}
                        </span>
                    ))}
                </div>
            </div>
            <Button
                size="sm"
                variant={blocked ? "outline" : "default"}
                disabled={busy}
                onClick={() => onProcess(item)}
                className="shrink-0"
            >
                {busy ? "Working…" : actionLabel}
                {!busy && <ArrowRight className="h-3 w-3" />}
            </Button>
        </div>
    );
}

export default function CommandCenterIntakeQueue() {
    const navigate = useNavigate();
    const qc = useQueryClient();

    const queue = useQuery({
        queryKey: ["intake", "queue"],
        queryFn: () => api.get<IntakeQueueResponse>("/api/intake/queue"),
        staleTime: 30_000,
    });

    function handleProcess(item: IntakeQueueItem) {
        if (item.source === "work_order") {
            // Work orders open the full-screen setup page (they are also grouped
            // in WorkOrderQueueSummary above and filtered out of this list).
            navigate(`/jobs/work-orders/setup/${item.id}`);
            return;
        }
        // Leads / bookings / calls reuse the prefilled job form — no new
        // create endpoints. Prefill the customer when we recognized one.
        const qs = item.customerId ? `?customerId=${encodeURIComponent(item.customerId)}` : "";
        navigate(`/jobs/new${qs}`);
    }

    // Work orders are now shown grouped (ready-to-confirm / needs-review) by
    // WorkOrderQueueSummary above; drop them from the generic list so they
    // aren't listed twice. Other sources (leads/bookings/calls) stay here.
    const items = (queue.data?.items ?? []).filter((it) => it.source !== "work_order");

    return (
        <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-semibold uppercase tracking-wider text-foreground/80">
                        Pending Intake
                    </CardTitle>
                    {items.length > 0 && (
                        <Badge variant="secondary" className="tabular-nums">
                            {items.length}
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    {FUTURE_SOURCES.map(({ label, icon: Icon }) => (
                        <span
                            key={label}
                            title={`${label} intake — coming soon`}
                            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                        >
                            <Icon className="h-3 w-3" />
                            {label}
                            <span className="text-[9px] uppercase">soon</span>
                        </span>
                    ))}
                </div>
            </CardHeader>
            <CardContent>
                <WorkOrderIntakeDropzone
                    onChanged={() => {
                        void qc.invalidateQueries({ queryKey: ["intake", "queue"] });
                        void qc.invalidateQueries({ queryKey: ["work-order-intake", "queue-summary"] });
                    }}
                />
                <WorkOrderQueueSummary />
                {queue.isError && (
                    <div className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 dark:text-amber-100">
                        Could not load the intake queue. Check that you are signed in and the API is reachable.
                    </div>
                )}
                {queue.isLoading ? (
                    <div className="space-y-2">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="h-20 animate-pulse rounded-lg bg-secondary/60" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="py-10 text-center">
                        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                            <Inbox className="h-6 w-6 text-primary" />
                        </div>
                        <p className="text-sm text-foreground/80">
                            Nothing waiting. New work orders, leads, bookings, and calls will land here.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {items.map((item) => (
                            <IntakeRow
                                key={`${item.source}:${item.id}`}
                                item={item}
                                onProcess={handleProcess}
                                busy={false}
                            />
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
